import { Fragment, useEffect, useRef, useState } from "react";
import type { Appointment, ChairRoster, DayFrame, ExternalBlock } from "@dentacore/shared-types";

import type { BlockState } from "./block-state";
import type { DaySpan, MoveProposal } from "./grid-geometry";
import { stateOf, useAwaitingShown } from "./block-state";
import styles from "./day-grid.module.css";
import { CardBody, cardColour } from "./booking-card";
import {
  CARD_MIN_PX,
  GUTTER_PX,
  MIN_HOUR_PX,
  packLanes,
  SNAP_MINUTES,
  blocksTheChair,
  clockOfMinutes,
  isMovable,
  nowLineMinutes,
  placeOnDay,
  placeSpanOnDay,
  proposeMove,
  spanFor,
  WAITLIST_DRAG_TYPE,
} from "./grid-geometry";
import type { Capacity, HourWindow, Zoom } from "./hour-window";
import { hoursBetween, useDayWindow } from "./hour-window";

/**
 * The calendar's day view — one column per chair, minute-accurate.
 *
 * ── What it draws, and what draws it ────────────────────────────────────────
 *
 * The look is the mockup's, exactly: 1.5px per minute, a 58px time gutter with
 * hour rules and dotted half-hours, columns separated by 8px, blocks inset 3px
 * carrying a 3px colour spine, and a warm now-line with a dot in the gutter.
 *
 * **None of the mockup's numbers about a clinic survive.** Its grid runs 08:00
 * to 18:00 over three chairs called "Chair 1/2/3" with "Dr. Haddad" on the
 * first, and every one of those is invented (§3.9). The span comes from
 * `frame`, the columns from `chairs`, and each header is derived server-side
 * from that day's rota.
 *
 * ── Nothing here decides anything ───────────────────────────────────────────
 *
 * No conflict rule and no clamping. The grid renders what the server said, and
 * the judgement it does hold — where a minute sits, and how much of a turnover
 * is left to draw — lives in `grid-geometry.ts`, under test. Dragging is
 * STORY-034 and the detail panel is STORY-036; this builds neither.
 */

/**
 * How long a finger has to rest on a block before it may be dragged.
 *
 * Long enough that reading down the day never picks anything up, short enough
 * that somebody who means to move a booking is not left wondering. The usual
 * range for this gesture is 300–500ms.
 */
const HOLD_MS = 420;

/**
 * How far a finger may wander during that hold and still count as still.
 *
 * Past it the gesture is certainly a scroll, and the hold is abandoned rather
 * than allowed to fire under a finger that has already left.
 */
const HOLD_SLOP_PX = 10;

/**
 * One class per state, and the whole of the seam STORY-032 owes STORY-056:
 * adding a state is a case in `stateOf` and a rule in the stylesheet. Nothing
 * in this component branches on a status.
 *
 * `booked`, `confirmed` and `checked-in` carry no class: since CF-D3 the
 * category colour dresses the card and the confirmation circle says the rest.
 */
const STATE_CLASS: Record<BlockState, string | undefined> = {
  booked: undefined,
  // Confirmed and checked in look like any booking: the filled circle says it (CF-D4).
  confirmed: undefined,
  "checked-in": undefined,
  "in-chair": styles.inChair,
  completed: styles.completed,
  awaiting: styles.awaiting,
  held: styles.held,
  "no-show": styles.noShow,
};

/**
 * States that draw a mark as well as a fill — the second half of the same seam.
 *
 * **A block's colour is not allowed to be its only cue.** STORY-023's
 * colour-vision constraint is inherited by any state whose whole job is to be
 * noticed, and `awaiting` is the first of those: an unvalidated booking that
 * looks validated makes the staff step decorative (STORY-056). Hue is the
 * channel colour blindness takes away, so the stylesheet adds texture and this
 * adds shape.
 *
 * It is a map rather than a check on `awaiting` on purpose. The component still
 * knows nothing about statuses or flags — it asks what state a block is in and
 * draws what that state carries, which is the property ADR-0004 asked STORY-032
 * to leave behind and the reason this story needed no grid surgery at all.
 */
const STATE_MARK: Partial<Record<BlockState, string>> = {
  awaiting: "Awaiting validation",
};

/**
 * What a block says in words about where the patient is. Every block is white
 * now (Anthony, 2026-10-11), so a fill no longer carries it; `booked` is the
 * ordinary state and says nothing.
 */
const STATE_LABEL: Partial<Record<BlockState, string>> = {
  confirmed: "Confirmed",
  "checked-in": "Checked in",
  "in-chair": "In the chair",
  completed: "Completed",
  awaiting: "Awaiting validation",
  "no-show": "No-show",
};

/** Below this much room the grid scrolls, as on a phone (FS-D1). */
const FLOOR_PX = 320;
/** On a touch screen: two whole hours at the minimum hour height (CZ-D4, OQ-D32). */
const TOUCH_FLOOR_PX = 240;

export interface DayGridProps {
  date: string;
  frame: DayFrame;
  chairs: readonly ChairRoster[];
  appointments: readonly Appointment[];
  /**
   * Events on the chairs' Google calendars that DentaCore did not create
   * (G14, STORY-049b).
   *
   * **Its own list, not folded into `appointments`.** An external block has no
   * patient, no status and nothing to open, and every appointment path here —
   * selection, dragging, the turnover pass — would have to special-case it.
   * Two lists is the cheaper truth.
   *
   * Defaults to empty, so a caller with no Google connection passes nothing.
   */
  externalBlocks?: readonly ExternalBlock[];
  selectedId?: string | null;
  onSelect?: (appointment: Appointment) => void;
  /**
   * Commit a drag. Given the new start and the chair it landed in.
   *
   * **The grid does not decide whether the move is allowed** — it rejects an
   * obviously-illegal drop before the pointer is released, then hands the drop
   * to this, which asks the server. A drop the grid thought was fine can still
   * come back refused.
   */
  onMove?: (appointment: Appointment, startsAt: Date, chairId: string) => void;
  /**
   * Clicking, or clicking and dragging, empty space (client request,
   * 2026-08-24). Given the chair and the minutes chosen, snapped to
   * `SNAP_MINUTES`. **The grid does not book anything itself** — same
   * division of labour as `onMove`: this only says what was selected, and
   * the caller opens the booking form on it, patient and procedure still to
   * ask for.
   */
  onQuickBook?: (chairId: string, startMinute: number, endMinute: number) => void;
  /**
   * A waiting patient dropped onto a gap (Anthony's QA, item 14).
   *
   * **Says where, never books.** The caller opens the ordinary booking panel
   * with the patient and their own duration filled in — a drop is a proposal,
   * and the turnover and clash rules still have to answer it.
   *
   * Absent on the week and month views, which have no chair columns to drop
   * into: a day is the only view where "which chair, what time" is a position.
   */
  onDropWaiting?: (entryId: string, chairId: string, startMinute: number) => void;
  /** Injectable so a test can pin the now-line rather than race the clock. */
  now?: Date;
  /**
   * A booking's clinician by name, for the card's second line (CF-D3): in a
   * chair's column the chair is already said, so the card names who treats.
   */
  providerName?: (providerId: string | null) => string | null;
  /**
   * A chair's name is a shortcut to *Only this chair* in the filters (CF-D16).
   * `onlyChairId` is the chair showing alone, so its head reads as pressed.
   */
  onChairName?: (chairId: string) => void;
  onlyChairId?: string | null;
  /** How many hours the day shows at once (CZ-D1); null or absent is the whole day. */
  hoursShown?: Zoom;
  /** Told how many hours fit at the minimum hour height, and how long the day is (CZ-D4). */
  onCapacity?: (capacity: Capacity) => void;
  /**
   * **One doctor's day in one column** (CZ-D6, Emile, 2026-10-21): when the
   * filters are down to a single doctor, their bookings stand in one column,
   * each naming its chair. No dragging there — there are no chairs to drop on —
   * and the column's head goes back to every doctor.
   */
  soloDoctor?: { id: string; name: string; where: string; onShowAll: () => void } | null;
}

/** The one column a single doctor's day is drawn in (CZ-D6); its id is no chair's. */
const SOLO_COLUMN: ChairRoster = { chairId: "", chairName: "", assignments: [], header: "" };

/** How far the last hour's label hangs below the grid's bottom line. */
const LABEL_OVERHANG_PX = 10;

/** A wheel's travel that pages the window by an hour. */
const WHEEL_PER_HOUR = 120;

export function DayGrid({
  date,
  frame,
  chairs,
  appointments,
  externalBlocks = [],
  selectedId = null,
  onSelect,
  onMove: onMoveAnywhere,
  onQuickBook,
  onDropWaiting: onDropWaitingAnywhere,
  now,
  providerName,
  onChairName,
  onlyChairId = null,
  hoursShown = null,
  onCapacity,
  soloDoctor = null,
}: DayGridProps): React.JSX.Element {
  /**
   * The drag in progress, if there is one.
   *
   * Held here rather than in each block because a cross-chair drag leaves the
   * column it started in — the block has to be drawn by whichever column it is
   * currently over, so no single block can own the state.
   */
  const [drag, setDrag] = useState<Drag | null>(null);
  /** The empty-space selection in progress, if there is one. Distinct from
   * `drag` above: that one moves an existing appointment, this one has not
   * asked the server for anything yet and never leaves its own column. */
  const [select, setSelect] = useState<QuickSelect | null>(null);
  const columns = useRef<HTMLDivElement | null>(null);

  /**
   * A touch that has not yet decided whether it is a scroll or a pick-up.
   *
   * ── The hazard this closes ─────────────────────────────────────────────────
   *
   * Dragging worked on touch from the day it shipped — pointer events and
   * `setPointerCapture` cover a finger without a second code path, and a
   * measured touch drag moves a booking and saves it. **That was the problem.**
   *
   * `.draggable` carries `touch-action: none`, so the browser never scrolls
   * from a block; and on a phone the day is 900px tall and blocks cover much of
   * it. **The ordinary gesture for reading down the day is a swipe**, and a
   * swipe that begins on an appointment was not a swipe — it was a reschedule,
   * committed on release, of a real patient's visit, by somebody who meant to
   * look at the afternoon. With a mouse this could not happen: nobody scrolls
   * by pressing on things.
   *
   * ── What replaces it ───────────────────────────────────────────────────────
   *
   * **A touch has to ask.** Hold still for `HOLD_MS` and the block arms and can
   * be dragged; move before that and the gesture is a scroll, which this pans
   * by hand — `touch-action: none` means the browser will not do it for us, and
   * a block you cannot scroll past is its own defect.
   *
   * A mouse or a stylus is unchanged and arms immediately: they were never
   * ambiguous.
   */
  const holding = useRef<Hold | null>(null);

  // The location's chosen hours (STORY-177), stretched by whole hours for any
  // booking or Google block of the day that falls outside them, so nothing is
  // ever hidden. The scale is the location's zoom.
  const occupied = [
    ...appointments.flatMap((appointment) => {
      const placed = placeOnDay(appointment, frame.timezone, date);
      return placed === null || !blocksTheChair(appointment) ? [] : [placed];
    }),
    ...externalBlocks.flatMap((block) => {
      const placed = placeSpanOnDay(block, frame.timezone, date);
      return placed === null ? [] : [placed];
    }),
  ];
  const zoomed = spanFor({
    opensAt: frame.calendarOpensAt,
    closesAt: frame.calendarClosesAt,
    zoom: frame.calendarZoom,
    occupied,
  });

  /**
   * **Today opens on the hour you are in** (FX14); any other day opens at the
   * top, where its hours begin. Once per date, so scrolling somewhere and then
   * dragging a block does not throw you back.
   */
  const scroller = useRef<HTMLDivElement | null>(null);
  const landedOn = useRef<string | null>(null);

  /**
   * **The grid ends where the screen ends** (Anthony, 2026-10-11): a fixed
   * `calc(100vh - 200px)` was wrong whenever the header, banner or key above it
   * was taller than assumed, and the page scrolled behind the grid. So the
   * height it may take is measured — from where it starts to the window's
   * bottom — and re-measured when the window or anything above it changes size.
   * Taller than that, the grid scrolls inside itself.
   */
  const [fitPx, setFitPx] = useState<number | null>(null);
  /** The window leaves less than the 320px floor under the grid's top: it scrolls. */
  const [floored, setFloored] = useState(false);
  /** The chair headers' height, which comes off the room the hours may take. */
  const [headsPx, setHeadsPx] = useState(0);
  /** The grid's own padding, border and any sideways scrollbar, which the hours cannot use. */
  const [chromePx, setChromePx] = useState(0);
  const heads = useRef<HTMLDivElement | null>(null);

  /**
   * **The whole day fits the window** (Emile, 2026-10-17: *"please always
   * calculate to fit screen size … for any screen size"*), reversing H3's "the
   * whole day is reachable by scrolling". Where the location's zoom would draw
   * the day taller than the room under the headers, the hours shrink until it
   * fits; where it already fits, the zoom stands. Only below the 320px floor —
   * a phone held sideways — does the grid still scroll.
   */
  // Less the last hour's label, which is centred on the bottom line and hangs below it.
  const room = fitPx === null ? null : fitPx - headsPx - chromePx - LABEL_OVERHANG_PX;
  // Below the floor the hours keep the location's scale and the grid scrolls
  // (FS-D1) — shrunk to fit there, the end of the day sat off the screen.
  const fitRoom = !floored && room !== null && room > 0 ? room : null;

  // **Never shorter than the minimum hour** (CZ-D4): what does not fit at it is
  // paged, like a zoom (CZ-D1). Below the floor (a phone) the grid scrolls as before.
  const capacity: Capacity | null =
    fitRoom === null
      ? null
      : {
          fits: Math.max(1, Math.floor(fitRoom / MIN_HOUR_PX)),
          day: (zoomed.endsAt - zoomed.startsAt) / 60,
        };
  const {
    window: hourWindow,
    page,
    pageable,
  } = useDayWindow(zoomed, hoursShown, capacity, date, nowLineMinutes(frame, zoomed, date, now));
  const wheel = useRef(0);
  useEffect(() => {
    if (capacity !== null) onCapacity?.(capacity);
  }, [capacity?.fits, capacity?.day]);

  const span: DaySpan =
    hourWindow !== null
      ? windowSpan(hourWindow, fitRoom, zoomed.pxPerMinute)
      : fitRoom !== null && zoomed.heightPx > fitRoom
        ? {
            ...zoomed,
            heightPx: fitRoom,
            pxPerMinute: fitRoom / (zoomed.endsAt - zoomed.startsAt),
          }
        : zoomed;

  useEffect(() => {
    const box = scroller.current;
    if (box === null) return;

    const fit = (): void => {
      // The content area's own bottom (less its padding) where there is one: the
      // grid's box ends there, not at the window's edge.
      const main = box.closest("main");
      // **Where the grid starts as laid out, not as scrolled to.** On a phone
      // the content area scrolls, and measured from its scrolled position the
      // room under the grid grew with every scroll — so the next re-measure
      // (picking a booking up changes a class, which schedules one) could flip
      // the grid out of its scrolling floor into a fitted, paged day mid-drag.
      const top = box.getBoundingClientRect().top + window.scrollY + (main?.scrollTop ?? 0);
      const mainBottom =
        main === null
          ? window.innerHeight - 12
          : main.getBoundingClientRect().bottom +
            window.scrollY -
            parseFloat(getComputedStyle(main).paddingBottom);
      // **Never past the window's own height.** Where the page itself scrolls —
      // a phone, whose toolbar puts the grid below the fold — `main` is as tall
      // as its content, the grid included, so measuring to its bottom fed the
      // grid's height back into its own room. The scale flipped between the
      // scrolling floor and a fitted, paged day as the page reflowed, and a
      // touch drag mid-flip was clamped to a window that did not hold the
      // booking: it landed hours earlier (e2e `touch-drag`, 2026-10-23).
      const bottom = Math.min(mainBottom, window.innerHeight);
      // **A tablet held sideways still pages** (OQ-D32): its finger-sized toolbar
      // leaves a little less room, so its floor is two whole hours, not 320px.
      const floor = document.documentElement.dataset.touch === "true" ? TOUCH_FLOOR_PX : FLOOR_PX;
      const room = Math.max(floor, Math.floor(bottom - top));
      setFitPx((previous) => (previous === room ? previous : room));
      setFloored(Math.floor(bottom - top) < floor);
      const headHeight = heads.current?.offsetHeight ?? 0;
      setHeadsPx((previous) => (previous === headHeight ? previous : headHeight));
      const style = getComputedStyle(box);
      const chrome = Math.ceil(
        box.offsetHeight -
          box.clientHeight +
          parseFloat(style.paddingTop) +
          parseFloat(style.paddingBottom),
      );
      setChromePx((previous) => (previous === chrome ? previous : chrome));
    };

    // **Re-measured whenever the page changes shape**, not only when a size
    // changes: the category key arrives after the grid has drawn and pushes it
    // down, yet every ancestor is a fixed-height box whose own size never moves,
    // so a ResizeObserver on them saw nothing. A mutation anywhere in the page
    // (a node added, a class or `hidden` flipped) schedules one measurement shortly after; `fit` ignores a result that has not changed, so this settles.
    let frame = 0;
    const schedule = (): void => {
      if (frame !== 0) return;
      frame = window.setTimeout(() => {
        frame = 0;
        fit();
      }, 30);
    };

    fit();
    window.addEventListener("resize", schedule);
    const watcher = new MutationObserver(schedule);
    watcher.observe(document.body, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ["class", "hidden"],
    });

    return () => {
      window.removeEventListener("resize", schedule);
      watcher.disconnect();
      if (frame !== 0) window.clearTimeout(frame);
    };
  }, []);

  useEffect(() => {
    if (landedOn.current === date) return;
    const box = scroller.current;
    if (box === null) return;

    landedOn.current = date;
    const current = nowLineMinutes(frame, span, date, now);
    // The start of the current hour sits just under the chair headers, so the
    // hour already under way and what is next are both on screen.
    box.scrollTop =
      current === null ? 0 : Math.max(0, offsetPx(Math.floor(current / 60) * 60, span) - 8);
    // Only the date decides when this runs; the frame and span are read as they
    // are at that moment, and re-running on them would undo the reader's scroll.
  }, [date]);

  const chairIds = chairs.map((chair) => chair.chairId);
  const chairNameOf = new Map(chairs.map((chair) => [chair.chairId, chair.chairName]));
  // One doctor's column has no chair heads, nothing to drag to and nowhere to drop.
  const columnsShown = soloDoctor === null ? chairs : [];
  const onMove = soloDoctor === null ? onMoveAnywhere : undefined;
  const onDropWaiting = soloDoctor === null ? onDropWaitingAnywhere : undefined;

  /**
   * Pick a block up.
   *
   * **Pointer events, not mouse events**, so a drag works with a trackpad, a
   * touchscreen and a stylus without three code paths — and `setPointerCapture`
   * means the block keeps receiving moves even when the pointer leaves it,
   * which it does constantly during a cross-chair drag.
   *
   * The column width is measured **now**, from the DOM, rather than assumed:
   * how wide a chair's column is depends on the window and on how many chairs
   * the branch has, and neither is knowable in advance (§6.1 asks for exactly
   * this).
   */
  const arm = (appointment: Appointment, pointerId: number, x: number, y: number): void => {
    const width = (columns.current?.clientWidth ?? 0) / Math.max(chairIds.length, 1);

    setDrag({
      id: appointment.id,
      pointerId,
      columnWidth: width,
      proposal: proposeMove({
        appointment,
        all: appointments,
        chairIds,
        frame,
        span,
        date,
        dx: 0,
        dy: 0,
        columnWidth: width,
      }),
      originX: x,
      originY: y,
      moved: false,
    });
  };

  /** Let go of a hold without arming it, whatever the reason. */
  const forgetHold = (): void => {
    if (holding.current !== null) clearTimeout(holding.current.timer);
    holding.current = null;
  };

  // A pending hold must not outlive the grid — the timer would arm a drag on a
  // component that has gone, which React reports as a state update on an
  // unmounted tree and a person sees as nothing at all.
  useEffect(() => forgetHold, []);

  const startDrag = (
    appointment: Appointment,
    event: React.PointerEvent<HTMLButtonElement>,
  ): void => {
    if (onMove === undefined || !isMovable(appointment)) return;

    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);

    /**
     * **A mouse and a stylus arm on contact; a finger has to hold.**
     *
     * They were never ambiguous — nobody scrolls a page by pressing on things
     * with a mouse. A finger is how you both pick a thing up *and* read down
     * the day, and the block cannot tell which is meant until it sees whether
     * the finger stays still. See `holding`.
     */
    if (event.pointerType !== "touch") {
      arm(appointment, event.pointerId, event.clientX, event.clientY);
      return;
    }

    forgetHold();
    holding.current = {
      appointment,
      pointerId: event.pointerId,
      originX: event.clientX,
      originY: event.clientY,
      lastY: event.clientY,
      scrolling: false,
      timer: setTimeout(() => {
        const held = holding.current;
        if (held === null || held.scrolling) return;
        holding.current = null;
        arm(held.appointment, held.pointerId, held.originX, held.originY);
      }, HOLD_MS),
    };
  };

  const onPointerMove = (appointment: Appointment, event: React.PointerEvent): void => {
    /**
     * **A finger that is still deciding scrolls the day itself.**
     *
     * `.draggable` sets `touch-action: none`, so the browser will not pan from
     * a block — which is what makes the hold gate possible and also means a
     * block you cannot scroll past would be its own defect. Once the finger has
     * travelled past the slop the gesture is certainly a scroll, the hold is
     * abandoned, and the rest of it pans the container by hand.
     */
    const held = holding.current;
    if (held !== null && held.appointment.id === appointment.id) {
      const wandered =
        Math.abs(event.clientX - held.originX) > HOLD_SLOP_PX ||
        Math.abs(event.clientY - held.originY) > HOLD_SLOP_PX;

      if (!held.scrolling && wandered) {
        clearTimeout(held.timer);
        held.scrolling = true;
      }

      if (held.scrolling) {
        const box = scroller.current;
        if (box !== null) box.scrollTop -= event.clientY - held.lastY;
      }

      held.lastY = event.clientY;
      return;
    }

    if (drag === null || drag.id !== appointment.id) return;

    const dx = event.clientX - drag.originX;
    const dy = event.clientY - drag.originY;

    setDrag({
      ...drag,
      // A click is a drag of nothing. Below this the block is being selected,
      // not moved, and releasing must not write anything.
      moved: drag.moved || Math.abs(dx) > 3 || Math.abs(dy) > 3,
      proposal: proposeMove({
        appointment,
        all: appointments,
        chairIds,
        frame,
        span,
        date,
        dx,
        dy,
        columnWidth: drag.columnWidth,
      }),
    });
  };

  const endDrag = (appointment: Appointment): void => {
    /**
     * **A finger lifted before the hold fired.** Either it was a tap — open the
     * booking, exactly as a click does — or it was a scroll, which has already
     * happened and wants nothing more. Nothing is ever written from here: a
     * gesture that never armed cannot have moved anything.
     */
    const held = holding.current;
    if (held !== null && held.appointment.id === appointment.id) {
      const scrolled = held.scrolling;
      forgetHold();
      if (!scrolled) onSelect?.(appointment);
      return;
    }

    if (drag === null || drag.id !== appointment.id) {
      setDrag(null);
      return;
    }

    const { proposal, moved } = drag;
    setDrag(null);

    if (!moved) {
      onSelect?.(appointment);
      return;
    }

    // **An illegal drop is simply not committed.** The block snaps back to where
    // it was, which is what "conflicts rejected during the drag rather than
    // after the drop" means in practice (§6.1).
    if (!proposal.legal) return;

    const placed = placeOnDay(appointment, frame.timezone, date);
    if (placed === null || proposal.startsAt === placed.startsAt) {
      const sameChair = chairIds[proposal.chairIndex] === appointment.chairId;
      if (sameChair) return;
    }

    onMove?.(
      appointment,
      instantOf(appointment, date, frame, proposal.startsAt),
      chairIds[proposal.chairIndex] ?? appointment.chairId,
    );
  };

  /**
   * Press down on empty space to start selecting a new booking's time.
   *
   * **`event.target !== event.currentTarget` is the whole guard.** A block
   * and a turnover strip both sit inside the column as absolutely-positioned
   * children; the strip already ignores the pointer (`pointer-events: none`
   * in CSS) but a block does not, so a press that actually landed on a block
   * has to be told apart from one that landed on the column behind it — the
   * block's own handler is what runs for the first case, and does not need
   * telling to stop.
   */
  const startSelect = (
    chair: ChairRoster,
    chairIndex: number,
    event: React.PointerEvent<HTMLDivElement>,
  ): void => {
    if (onQuickBook === undefined || event.target !== event.currentTarget) return;

    event.currentTarget.setPointerCapture(event.pointerId);
    const top = event.currentTarget.getBoundingClientRect().top;
    const startMinute = snapToGrid(span.startsAt + (event.clientY - top) / span.pxPerMinute);

    setSelect({
      chairId: chair.chairId,
      chairIndex,
      pointerId: event.pointerId,
      columnTop: top,
      startMinute,
      endMinute: startMinute + SNAP_MINUTES,
    });
  };

  /**
   * Where the pointer is over a column, as the minute a click there would
   * book — so a Ctrl + click (the quick ring, STORY-221) knows which slot it
   * is on without a click of its own. Written straight onto the element: it
   * changes nothing drawn, so it is not state.
   */
  const noteRingMinute = (event: React.PointerEvent<HTMLDivElement>): void => {
    const top = event.currentTarget.getBoundingClientRect().top;
    const minute = snapToGrid(span.startsAt + (event.clientY - top) / span.pxPerMinute);
    event.currentTarget.dataset.ringMinute = String(minute);
  };

  /** Drag extends the end in `SNAP_MINUTES` steps; the start never moves. */
  const moveSelect = (event: React.PointerEvent<HTMLDivElement>): void => {
    if (select === null || select.pointerId !== event.pointerId) return;

    const minute = snapToGrid(
      span.startsAt + (event.clientY - select.columnTop) / span.pxPerMinute,
    );
    const endMinute = Math.min(span.endsAt, Math.max(select.startMinute + SNAP_MINUTES, minute));

    setSelect({ ...select, endMinute });
  };

  const endSelect = (event: React.PointerEvent<HTMLDivElement>): void => {
    if (select === null || select.pointerId !== event.pointerId) return;

    const { chairId, startMinute, endMinute } = select;
    setSelect(null);
    onQuickBook?.(chairId, startMinute, endMinute);
  };

  const nowAt = nowLineMinutes(frame, span, date, now);
  const height = `${String(span.heightPx)}px`;
  const gutter = `${String(GUTTER_PX)}px`;

  /** A wheel over a zoomed-in day pages it an hour at a time. */
  const onWheel = (event: React.WheelEvent): void => {
    if (!pageable) return;
    wheel.current += event.deltaY;
    if (Math.abs(wheel.current) < WHEEL_PER_HOUR) return;
    page(wheel.current > 0 ? 1 : -1, 60);
    wheel.current = 0;
  };

  return (
    <div
      className={styles.grid}
      ref={scroller}
      style={fitPx === null ? undefined : { maxHeight: `${String(fitPx)}px` }}
      data-testid="day-grid"
      data-hours-shown={hoursShown ?? "day"}
      data-opens={span.startsAt}
      data-closes={span.endsAt}
      // The scale the hours are drawn at, once the grid has measured its room:
      // since CZ-D1 and CZ-D4 it follows the window, not only the zoom, so
      // tests read it here rather than restating it.
      data-px-per-minute={fitPx === null ? undefined : span.pxPerMinute}
      // The branch's own hours, kept on the element for tests and for anything
      // that wants to know what the clinic configured. **Drag no longer clamps
      // to these** — since H2 hours refuse nothing, so clamping to them would
      // stop a drag the server would have accepted. It clamps to the drawn
      // span above instead.
      data-branch-opens={frame.opensAt ?? undefined}
      data-branch-closes={frame.closesAt ?? undefined}
    >
      <div className={styles.heads} ref={heads}>
        <div className={styles.headSpacer} style={{ width: gutter }}>
          {pageable && hourWindow !== null && <HourPager window={hourWindow} onPage={page} />}
        </div>
        {soloDoctor !== null && (
          <div className={styles.head} data-testid="doctor-column-head">
            <div className={styles.headLine}>
              <button
                type="button"
                className={styles.colButton}
                aria-pressed="true"
                title="Show every doctor"
                onClick={soloDoctor.onShowAll}
                data-testid="column-only"
              >
                <span className={styles.chairName}>{soloDoctor.name}</span>
              </button>
            </div>
            <div className={styles.headSub}>{soloDoctor.where}</div>
          </div>
        )}
        {columnsShown.map((chair) => (
          <div key={chair.chairId} className={styles.head} data-testid="chair-column-head">
            <div className={styles.headLine}>
              {/* `.chairName` truncates to fit its column, and eight chairs on a
                  1440px screen leaves ~90px each — enough to cut every name. The
                  title attribute is what makes the full one reachable.

                  **The name is a shortcut to "Only this chair"** (CF-D16), and
                  pressing it again shows every chair. */}
              {onChairName === undefined ? (
                <span className={styles.chairName} title={chair.chairName}>
                  {chair.chairName}
                </span>
              ) : (
                <button
                  type="button"
                  className={styles.colButton}
                  aria-pressed={onlyChairId === chair.chairId}
                  title={
                    onlyChairId === chair.chairId
                      ? "Show every chair"
                      : `Show only ${chair.chairName}`
                  }
                  onClick={() => onChairName(chair.chairId)}
                  data-testid="column-only"
                >
                  <span className={styles.chairName}>{chair.chairName}</span>
                </button>
              )}
            </div>
            {/* The rota, or nothing — a chair nobody is on still gets a column. */}
            <div className={styles.headSub}>{subtitleOf(chair)}</div>
          </div>
        ))}
      </div>

      <div className={styles.body} onWheel={onWheel}>
        <div className={styles.axis} style={{ width: gutter, height }}>
          {span.hours.map((minute) => (
            <div
              key={minute}
              className={styles.axisLabel}
              style={{ top: `${String(offsetPx(minute, span) - 7)}px` }}
            >
              {clockOfMinutes(minute)}
            </div>
          ))}
          {/* The now line's time, on the clock's edge (CF-D17). */}
          {nowAt !== null && (
            <span
              className={styles.nowTag}
              style={{ top: `${String(offsetPx(nowAt, span))}px` }}
              data-testid="now-tag"
            >
              {clockOfMinutes(Math.floor(nowAt))}
            </span>
          )}
        </div>

        <div
          // A booking running across the window's edge is cut there, not drawn over the heads.
          className={
            hourWindow === null
              ? styles.columns
              : `${styles.columns ?? ""} ${styles.windowed ?? ""}`
          }
          style={{ height }}
          ref={columns}
        >
          {/*
            **A fragment, not a wrapping div** (client's manual QA, item 12).

            `.columns` is a flex row with an 8px gap, and these were wrapped in a
            `<div>` each — so every hour was a **flex item**, and the gaps
            between them pushed the first chair column sideways. The lines
            inside are absolutely positioned, so the wrappers had no width of
            their own and the damage was purely the gaps: 8px times one fewer
            than the number of hours.

            It has always been wrong. It was 80px while the axis drew ten hours
            and nobody saw it; T7 made the axis twenty-four and it became 192px —
            most of a column — which is when the client reported the headers
            standing beside their columns rather than over them. Centring the
            header text, which is what I changed first, was treating the symptom.
          */}
          {span.hours.map((minute) => (
            <Fragment key={minute}>
              <div
                className={styles.hourLine}
                style={{ top: `${String(offsetPx(minute, span))}px` }}
              />
              {minute < span.endsAt && (
                <div
                  className={styles.halfLine}
                  style={{ top: `${String(offsetPx(minute + 30, span))}px` }}
                />
              )}
            </Fragment>
          ))}

          {/*
            **The part of today that has gone** (client's manual QA, item 1;
            made plainly grey by FX14 — at 5% it read as white).
            A wash over everything above the now-line, so the day reads at a
            glance as *this much is behind us* rather than as a uniform white
            sheet the client described. Drawn before the now-line and before the
            columns so it tints the empty grid and never dims a booking — a past
            appointment is still a record somebody needs to read.

            Only on today. Yesterday is entirely past and shading all of it says
            nothing; next Tuesday has no past at all.
          */}
          {nowAt !== null && (
            <div
              className={styles.past}
              style={{ height: `${String(offsetPx(nowAt, span))}px` }}
              data-testid="past-shade"
              aria-hidden="true"
            />
          )}

          {nowAt !== null && (
            <div
              className={styles.now}
              style={{ top: `${String(offsetPx(nowAt, span))}px` }}
              data-testid="now-line"
              data-minutes={nowAt}
            />
          )}

          {(soloDoctor === null ? chairs : [SOLO_COLUMN]).map((chair, index) => {
            // **Cards that would overlap as drawn stand side by side** (CZ-D3):
            // a card is never shorter than its two lines, so a short booking can
            // reach into the next one's time. A no-show keeps its strip (FX28).
            const held = appointments
              .filter(
                (appointment) => blocksTheChair(appointment) || appointment.status === "NO_SHOW",
              )
              .filter(
                (appointment) =>
                  // A chair held for nobody is not in a doctor's day.
                  (soloDoctor !== null && appointment.providerId === soloDoctor.id) ||
                  (soloDoctor === null && columnHolds(appointment, chair.chairId, chairIds, drag)),
              );
            const sideBySide = packLanes(
              held.flatMap((appointment) => {
                if (laneOf(appointment, held, frame.timezone, date) !== "full") return [];
                const placed = placeOnDay(appointment, frame.timezone, date);
                if (placed === null) return [];
                const startsAt =
                  drag !== null && drag.id === appointment.id
                    ? drag.proposal.startsAt
                    : placed.startsAt;
                const drawn = Math.max(
                  placed.endsAt - placed.startsAt,
                  CARD_MIN_PX / span.pxPerMinute,
                );
                return [{ id: appointment.id, startsAt, endsAt: startsAt + drawn }];
              }),
            );
            return (
              <div
                key={chair.chairId}
                className={styles.column}
                data-testid="chair-column"
                data-chair-id={chair.chairId}
                // Empty space is a slot to the quick ring (STORY-221); the
                // minute under the pointer rides on `data-ring-minute`.
                data-ring="cal.slot"
                onPointerDown={(event) => startSelect(chair, index, event)}
                onPointerMove={(event) => {
                  noteRingMinute(event);
                  moveSelect(event);
                }}
                onPointerUp={endSelect}
                /*
                  **Dropping a waiting patient into a gap** — Anthony's QA,
                  item 14: *"maybe let me drag the waitlist to the calendar"*.

                  **HTML5 drag-and-drop, deliberately not the pointer events
                  above.** Rescheduling is built on pointer capture, and
                  reaching across from a component in another column would mean
                  threading a pointer session through both — on the one
                  interaction in the product where a bug moves somebody's real
                  appointment. `dragstart`/`drop` is a separate channel the
                  browser arbitrates, so the two cannot interfere: a pointer
                  drag that becomes a native drag stops being a pointer drag.

                  The drop only computes *where*. It never books — the panel it
                  opens is the same one the desk fills in for any other booking,
                  because a patient dropped on a gap is a proposal and the
                  conflict rules still have to answer.
                */
                onDragOver={
                  onDropWaiting === undefined
                    ? undefined
                    : (event) => {
                        // Without this the browser refuses the drop outright —
                        // "no preventDefault" means "not a drop target".
                        if (!event.dataTransfer.types.includes(WAITLIST_DRAG_TYPE)) return;
                        event.preventDefault();
                        event.dataTransfer.dropEffect = "copy";
                      }
                }
                onDrop={
                  onDropWaiting === undefined
                    ? undefined
                    : (event) => {
                        const entryId = event.dataTransfer.getData(WAITLIST_DRAG_TYPE);
                        if (entryId === "") return;
                        event.preventDefault();

                        const top = event.currentTarget.getBoundingClientRect().top;
                        const minute = snapToGrid(
                          span.startsAt + (event.clientY - top) / span.pxPerMinute,
                        );

                        onDropWaiting(entryId, chair.chairId, clampToSpan(minute, span));
                      }
                }
              >
                {/*
                  **Events on this chair's Google calendar that we did not
                  create** (G14, STORY-049b). Drawn, blocking, and plainly not
                  an appointment: hatched rather than tinted, no status, no
                  patient, and nothing to click through to — because there is
                  nothing in DentaCore to open. Google is its source of truth,
                  so it is changed where it was made.

                  Drawn beneath the appointment blocks, like the turnover tails
                  above, for the same reason: an appointment is the thing
                  somebody is working with, and it should never be obscured.

                  **The pointer goes straight through** (`pointer-events: none`
                  in the stylesheet). Clicking a blocked slot still opens the
                  booking form, and the server refuses it by name — *"That chair
                  is blocked in Google by Renovation. The next free start in it
                  is 13:00."* Swallowing the click would be a second, weaker
                  implementation of a rule the conflict service already owns,
                  and it would silently stop working the moment the two
                  disagreed.
                */}
                {externalBlocks
                  .filter((block) => block.chairId === chair.chairId)
                  .map((block) => {
                    const placed = placeSpanOnDay(block, frame.timezone, date);
                    if (placed === null) return null;
                    // **The rules an appointment block already follows**
                    // (2026-10-12): the same floor, and the second line
                    // dropped — still said — when it cannot fit whole. At the
                    // default zoom a half-hour Google event clipped both lines.
                    const heightPx = Math.max(
                      (placed.endsAt - placed.startsAt) * span.pxPerMinute - 3,
                      MIN_BLOCK_PX,
                    );
                    const twoLines = heightPx >= MIN_BLOCK_PX_WITH_SUBTITLE;

                    return (
                      <div
                        key={block.id}
                        className={
                          twoLines
                            ? styles.externalBlock
                            : `${styles.externalBlock ?? ""} ${styles.externalShort ?? ""}`
                        }
                        data-testid="external-block"
                        data-block-id={block.id}
                        data-all-day={block.isAllDay ? "true" : "false"}
                        style={{
                          top: `${String(offsetPx(placed.startsAt, span))}px`,
                          height: `${String(heightPx)}px`,
                        }}
                      >
                        <span className={styles.externalTitle}>
                          {block.summary ?? "Busy in Google"}
                        </span>
                        <span className={twoLines ? styles.externalNote : styles.srOnly}>
                          {block.isAllDay
                            ? "All day · from Google"
                            : `${clockOfMinutes(placed.startsAt)}–${clockOfMinutes(placed.endsAt)} · from Google`}
                        </span>
                      </div>
                    );
                  })}

                {select !== null && select.chairIndex === index && (
                  <div
                    className={styles.quickSelect}
                    data-testid="quick-select"
                    data-starts={select.startMinute}
                    data-ends={select.endMinute}
                    style={{
                      top: `${String(offsetPx(select.startMinute, span))}px`,
                      height: `${String((select.endMinute - select.startMinute) * span.pxPerMinute - 3)}px`,
                    }}
                  >
                    <span className={styles.blockTitle}>
                      {clockOfMinutes(select.startMinute)}–{clockOfMinutes(select.endMinute)}
                    </span>
                  </div>
                )}

                {/*
                  **Not `own`.** A block being dragged across chairs has to be
                  drawn by whichever column it is currently over, not the one it
                  started in — so each column asks which appointments it holds
                  *right now*, drag included.
                */}
                {appointments
                  /**
                   * **A cancellation draws nothing** (2026-08-24) — the slot is
                   * free and the grid says so by being empty. **A no-show is
                   * drawn again, struck through** (FX28, reversing T2): it still
                   * frees the chair, and `laneOf` below narrows it to a strip
                   * whenever a live booking shares its time, so the two never
                   * overlap — which was T2's whole complaint.
                   *
                   * This was `status !== "CANCELLED"` and the omission was a
                   * real defect rather than an inconsistency. A no-show *also*
                   * frees the chair — both have always been in
                   * `NON_BLOCKING_STATUSES` — so a booking made into a missed
                   * slot was accepted by the server and then drawn on top of
                   * the block still sitting there. The client's words were
                   * "they can even intertwine".
                   *
                   * `blocksTheChair` is the same predicate `turnoverStripsForChair`
                   * uses, which is the point: one idea of "this still holds the
                   * chair", so the block and its turnover cannot disagree.
                   *
                   * The row survives in `appointments` for the turnover pass
                   * above and for the selected-panel lookup in
                   * `calendar-page.tsx`; only this render is filtered.
                   */
                  .filter(
                    (appointment) =>
                      blocksTheChair(appointment) || appointment.status === "NO_SHOW",
                  )
                  .filter(
                    (appointment) =>
                      // A chair held for nobody is not in a doctor's day.
                      (soloDoctor !== null && appointment.providerId === soloDoctor.id) ||
                      (soloDoctor === null &&
                        columnHolds(appointment, chair.chairId, chairIds, drag)),
                  )
                  .map((appointment, _at, column) => {
                    const placed = placeOnDay(appointment, frame.timezone, date);
                    if (placed === null) return null;
                    const lane = laneOf(appointment, column, frame.timezone, date);

                    const dragging = drag !== null && drag.id === appointment.id;
                    const startsAt = dragging ? drag.proposal.startsAt : placed.startsAt;

                    return (
                      <Block
                        key={appointment.id}
                        appointment={appointment}
                        // In one doctor's column the doctor is said; the chair is not (CZ-D6).
                        provider={
                          soloDoctor === null
                            ? (providerName?.(appointment.providerId) ?? null)
                            : (chairNameOf.get(appointment.chairId) ?? null)
                        }
                        startsAt={startsAt}
                        top={offsetPx(startsAt, span)}
                        minutes={placed.endsAt - placed.startsAt}
                        pxPerMinute={span.pxPerMinute}
                        selected={appointment.id === selectedId}
                        dragging={dragging}
                        // Only meaningful while dragging: a block at rest is
                        // neither legal nor illegal, it simply is.
                        illegal={dragging && !drag.proposal.legal}
                        lane={lane}
                        side={sideBySide.get(appointment.id) ?? [0, 1]}
                        // A missed visit is moved from its panel, never dragged:
                        // it sits in a strip, and picking it up would hide the
                        // live booking it shares the slot with.
                        draggable={
                          onMove !== undefined &&
                          isMovable(appointment) &&
                          appointment.status !== "NO_SHOW"
                        }
                        onPointerDown={startDrag}
                        onPointerCancel={forgetHold}
                        onPointerMove={onPointerMove}
                        onPointerUp={endDrag}
                        onSelect={onSelect}
                      />
                    );
                  })}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/**
 * Where a block sits across its column (FX28).
 *
 * A no-show frees the chair, so a live booking may be made over it. Drawn
 * full-width the two would overlap, which is exactly what T2 removed no-shows
 * to avoid; so while they share any minute the no-show shrinks to a struck
 * strip at the right edge and the live booking stops short of it.
 */
type Lane = "full" | "strip" | "beside";

function laneOf(
  appointment: Appointment,
  column: readonly Appointment[],
  timezone: string,
  date: string,
): Lane {
  const mine = placeOnDay(appointment, timezone, date);
  if (mine === null) return "full";
  const missed = appointment.status === "NO_SHOW";

  const shares = column.some((other) => {
    if (other.id === appointment.id) return false;
    // A no-show is narrowed by a live booking, and a live booking by a no-show;
    // two live bookings never share a chair, and two no-shows may stay as they are.
    if ((other.status === "NO_SHOW") === missed) return false;
    const theirs = placeOnDay(other, timezone, date);
    return theirs !== null && theirs.startsAt < mine.endsAt && theirs.endsAt > mine.startsAt;
  });

  if (!shares) return "full";
  return missed ? "strip" : "beside";
}

interface BlockProps {
  appointment: Appointment;
  /** Who treats, for the second line; null when nobody is named. */
  provider: string | null;
  startsAt: number;
  top: number;
  minutes: number;
  /** The grid's scale (STORY-177). */
  pxPerMinute: number;
  selected: boolean;
  dragging: boolean;
  /** Being dragged somewhere the server would refuse. */
  illegal: boolean;
  /** Full width, a no-show's strip, or a live booking beside one (FX28). */
  lane: Lane;
  /** Its lane among cards that overlap as drawn, and how many there are (CZ-D3). */
  side: [number, number];
  draggable: boolean;
  onPointerDown: (appointment: Appointment, event: React.PointerEvent<HTMLButtonElement>) => void;
  onPointerMove: (appointment: Appointment, event: React.PointerEvent) => void;
  onPointerUp: (appointment: Appointment) => void;
  onPointerCancel: () => void;
  onSelect?: (appointment: Appointment) => void;
}

/**
 * One booking card, positioned by minute and dressed by **state** rather than
 * by status (`block-state.ts`).
 *
 * **The card is CF-D3's** (2026-10-21), superseding the white block with
 * category edges on its top and right (Anthony, 2026-10-11 and 2026-10-14): a
 * faint tint of its procedure colour, wrapped in that colour, heavier on the
 * left. What it says is `CardBody`, shared with the combined calendar.
 */
function Block({
  appointment,
  provider,
  startsAt,
  top,
  minutes,
  pxPerMinute,
  selected,
  dragging,
  illegal,
  lane,
  side,
  draggable,
  onPointerDown,
  onPointerMove,
  onPointerCancel,
  onPointerUp,
  onSelect,
}: BlockProps): React.JSX.Element {
  const awaitingShown = useAwaitingShown();
  const state = stateOf(appointment, awaitingShown);
  const mark = STATE_MARK[state];
  // Four pixels short so consecutive cards read as two things, and never
  // shorter than both lines with room around them (CZ-D3).
  const heightPx = Math.max(minutes * pxPerMinute - 4, CARD_MIN_PX);
  const [sideLane, sideLanes] = side;

  const className = [
    styles.block,
    STATE_CLASS[state],
    selected ? styles.selected : undefined,
    dragging ? styles.dragging : undefined,
    illegal ? styles.illegal : undefined,
    lane === "strip" ? styles.strip : lane === "beside" ? styles.beside : undefined,
    draggable ? styles.draggable : undefined,
  ]
    .filter((name): name is string => name !== undefined && name !== "")
    .join(" ");

  return (
    <button
      type="button"
      // `appointment-row` is what STORY-029b's tests count, and these blocks are
      // what those rows became. Kept, so booking keeps proving itself.
      data-testid="appointment-row"
      // Not a control: it carries its status in its fill, so the global accent
      // hover must not repaint it. See `index.css`.
      data-card=""
      data-id={appointment.id}
      // A booking is a thing to the quick ring (STORY-221).
      data-ring="cal.visit"
      data-state={state}
      data-lane={lane}
      // A strip has no room for a name, so the name is the hover text.
      title={
        lane === "strip" && appointment.patient !== null
          ? `No-show · ${appointment.patient.firstName} ${appointment.patient.lastName}`
          : undefined
      }
      data-top={top}
      data-minutes={minutes}
      data-dragging={dragging ? "true" : undefined}
      data-illegal={illegal ? "true" : undefined}
      data-draggable={draggable ? "true" : undefined}
      className={className}
      style={{
        top: `${String(top)}px`,
        // Three pixels short, so consecutive blocks read as two things rather
        // than one. The mockup does the same.
        height: `${String(heightPx)}px`,
        ...(sideLanes > 1
          ? {
              left: `calc(${String((100 / sideLanes) * sideLane)}% + 3px)`,
              right: "auto",
              width: `calc(${String(100 / sideLanes)}% - 6px)`,
            }
          : {}),
        ...cardColour(appointment),
      }}
      // **Selection happens on pointer-up, not on click.** A drag ends with a
      // click too, and a block that selected itself every time it was moved
      // would open a panel nobody asked for. `endDrag` decides which it was,
      // from how far the pointer travelled.
      onPointerDown={draggable ? (event) => onPointerDown(appointment, event) : undefined}
      onPointerMove={draggable ? (event) => onPointerMove(appointment, event) : undefined}
      onPointerUp={
        draggable
          ? () => onPointerUp(appointment)
          : onSelect !== undefined
            ? () => onSelect(appointment)
            : undefined
      }
      /* The browser can take a gesture away — a phone call, a system sheet, a
         second finger. Whatever was pending goes with it. */
      onPointerCancel={draggable ? onPointerCancel : undefined}
    >
      {mark !== undefined && (
        <span
          className={styles.stateMark}
          data-testid="state-mark"
          title={mark}
          aria-hidden="true"
        />
      )}
      {/*
        **Too short to draw the second line, it is still said** (2026-10-12). At
        the default zoom (STORY-177) a half-hour block has no room for it, and a
        screen reader then heard only the patient — not what, when or in what
        state. Visually hidden, so nothing on the grid changes. A no-show's strip
        says all of it that way.
      */}
      {lane === "strip" ? (
        <span className={styles.srOnly}>
          {appointment.patient === null
            ? "Chair held"
            : `${appointment.patient.firstName} ${appointment.patient.lastName}`}{" "}
          · {clockOfMinutes(startsAt)} · No-show
        </span>
      ) : (
        <CardBody
          appointment={appointment}
          startMinute={startsAt}
          endMinute={startsAt + minutes}
          detail={provider}
          compact={false}
          roomForDetail
          stateLabel={STATE_LABEL[state]}
        />
      )}
    </button>
  );
}

/**
 * The floor on a block's drawn height.
 *
 * **Sized to fit the title line, not just to be a visible shape.** A
 * five-minute booking is 7.5px and a fifteen-minute one is 19.5px — both
 * shorter than `.short .blockTitle`'s own line box plus the block's padding
 * and border, so the title itself was being clipped by `overflow: hidden` on
 * the block, and a clinician scanning the grid could not tell whose booking
 * it was. 22px comfortably fits one line at the `.short` font size — measured
 * in a real browser at 3px padding + 1px border on each edge and a 12.64px
 * title line box, which leaves under 2px spare. **That spare is exactly what
 * used to let the subtitle bleed through as a clipped sliver** rather than
 * being cleanly absent; `MIN_BLOCK_PX_WITH_SUBTITLE` below is what stops
 * that, by not drawing the subtitle at all rather than by tightening this
 * number further. The grid stops being minute-accurate below this, the only
 * place on it where that trade is made, and it is made in favour of being
 * legible.
 */
const MIN_BLOCK_PX = 22;

/**
 * Below this, an external block's second line does not draw at all. Measured the same way as `MIN_BLOCK_PX`: a title line box of
 * 12.64px plus a subtitle line box of 12.09px plus 6px of padding and 2px of
 * border is 32.73px, so anything shorter cannot hold both without the second
 * line bleeding into the first the same way it used to bleed past the
 * bottom. Rounded up rather than down — the failure mode of drawing one
 * pixel too tall is a title with a little extra space beneath it, and the
 * failure mode of drawing one pixel too short is the exact bug this exists
 * to fix.
 */
const MIN_BLOCK_PX_WITH_SUBTITLE = 33;

function offsetPx(minute: number, span: DaySpan): number {
  return (minute - span.startsAt) * span.pxPerMinute;
}

/** The nearest `SNAP_MINUTES` mark — the same grid a drag already snaps to. */
function snapToGrid(minute: number): number {
  return Math.round(minute / SNAP_MINUTES) * SNAP_MINUTES;
}

/**
 * Keep a dropped minute inside the day the grid is drawing.
 *
 * A drop near the very bottom of a column rounds past the last mark, which
 * would open the booking form on a time the axis does not have. One snap step
 * of headroom at the end, so the start is always a time a booking can begin at.
 */
function clampToSpan(minute: number, span: { startsAt: number; endsAt: number }): number {
  return Math.min(Math.max(minute, span.startsAt), span.endsAt - SNAP_MINUTES);
}

/** `Chair 1 · Dana Haddad` minus the chair — the server already derived it. */
function subtitleOf(chair: ChairRoster): string {
  const [, rest] = chair.header.split(" · ");
  return rest ?? "";
}

/** A drag in progress. */
interface Drag {
  id: string;
  pointerId: number;
  /** Measured from the DOM when the drag began, not assumed. */
  columnWidth: number;
  proposal: MoveProposal;
  originX: number;
  originY: number;
  /**
   * Whether the pointer has travelled far enough to be a move rather than a
   * click. Below the threshold, releasing selects instead of writing.
   */
  moved: boolean;
}

/**
 * A finger resting on a block, before it has become a drag or a scroll.
 *
 * Only ever set for `pointerType === "touch"` — see `holding` in the grid.
 */
interface Hold {
  appointment: Appointment;
  pointerId: number;
  /** Where the finger landed, for measuring how far it has since travelled. */
  originX: number;
  originY: number;
  /** Where it was at the last move, for panning the day by the difference. */
  lastY: number;
  timer: ReturnType<typeof setTimeout>;
  /** Set once the finger has moved far enough that this is certainly a scroll. */
  scrolling: boolean;
}

/** An empty-space time selection in progress — the seed for a new booking. */
interface QuickSelect {
  chairId: string;
  chairIndex: number;
  pointerId: number;
  /** The column's own top, measured once at press rather than every move. */
  columnTop: number;
  /** Fixed once the press lands; only the end moves. */
  startMinute: number;
  endMinute: number;
}

/**
 * Does this column draw this appointment right now?
 *
 * Its own chair normally — but a block being dragged across chairs belongs to
 * whichever column the pointer is over, and to no other. Without the second
 * clause a cross-chair drag draws the block twice, in the column it left and
 * the column it is entering.
 */
function columnHolds(
  appointment: Appointment,
  chairId: string,
  chairIds: readonly string[],
  drag: Drag | null,
): boolean {
  if (drag !== null && drag.id === appointment.id) {
    return chairIds[drag.proposal.chairIndex] === chairId;
  }
  return appointment.chairId === chairId;
}

/**
 * A minute on the drawn day, back to the instant the server needs.
 *
 * **Reconstructed on the branch's clock**, not the reader's: the grid drew the
 * block at a wall-clock minute in the branch's zone, and sending the reader's
 * interpretation of that minute would move the booking by the offset between
 * them. Built by nudging a known instant on the same day rather than by parsing
 * a string, so daylight saving is the platform's problem rather than this
 * function's.
 */
function instantOf(appointment: Appointment, date: string, frame: DayFrame, minutes: number): Date {
  const placed = placeOnDay(appointment, frame.timezone, date);
  const from = new Date(appointment.startsAt);
  if (placed === null) return from;
  return new Date(from.getTime() + (minutes - placed.startsAt) * 60_000);
}

/**
 * The span a paged window draws (CZ-D1): its hours, filling the room the whole
 * day would have taken — which is what makes zooming in make bookings taller.
 * Without a measured room yet, the location's own scale.
 */
function windowSpan(window: HourWindow, room: number | null, pxPerMinute: number): DaySpan {
  const minutes = window.endsAt - window.startsAt;
  const heightPx = room ?? minutes * pxPerMinute;
  return {
    startsAt: window.startsAt,
    endsAt: window.endsAt,
    hours: hoursBetween(window.startsAt, window.endsAt),
    heightPx,
    pxPerMinute: heightPx / minutes,
  };
}

/**
 * Earlier and later hours, in the corner above the clock (CZ-D1). Each is
 * disabled at its end of the day, and says which hours it shows.
 */
export function HourPager({
  window,
  onPage,
}: {
  window: HourWindow;
  onPage: (direction: 1 | -1) => void;
}): React.JSX.Element {
  return (
    <div className={styles.pager} role="group" aria-label="Hours shown">
      <button
        type="button"
        className={styles.pagerButton}
        disabled={!window.earlier}
        onClick={() => onPage(-1)}
        aria-label="Earlier hours"
        title="Earlier hours"
        data-testid="hours-earlier"
      >
        <svg viewBox="0 0 12 12" aria-hidden="true">
          <path d="M3 7.5 6 4.5l3 3" />
        </svg>
      </button>
      <button
        type="button"
        className={styles.pagerButton}
        disabled={!window.later}
        onClick={() => onPage(1)}
        aria-label="Later hours"
        title="Later hours"
        data-testid="hours-later"
      >
        <svg viewBox="0 0 12 12" aria-hidden="true">
          <path d="M3 4.5 6 7.5l3-3" />
        </svg>
      </button>
    </div>
  );
}
